import { ItemDetailsData, RecommendedItem } from "./DefinitionsInterfaces";

// Coupon data interface
export interface CouponData {
  isApplied: boolean;
  code: string;
  discountAmount: number;
}

// Bundle info interface
export interface BundleInfo {
  isBundle: boolean;
  primaryProductId: number | null;
}

// Pricing calculation result interface
export interface PricingResult {
  basePrice: number;
  sellingPrice: number;
  discountedPrice: number;
  bundleDiscount: number;
  prepaidDiscount: number;
  couponDiscount: number;
  finalPrice: number;
  totalDiscount: number;
  discountPercentage: number;
}

// Individual item pricing result
export interface ItemPricingResult {
  itemId: number;
  basePrice: number;
  displayPrice: number;
  isBundlePriceApplied: boolean;
  discountAmount: number;
  discountPercentage: number;
}

// Cart pricing summary
export interface CartPricingSummary {
  items: ItemPricingResult[];
  subtotal: PricingResult;
  couponData: CouponData;
  bundleInfo: BundleInfo;
}

class PricingService {
  /**
   * Get coupon data from session storage
   */
  static getCouponData(): CouponData {
    const appliedCouponCode = sessionStorage.getItem("appliedCouponCode");
    const couponDiscountAmount = sessionStorage.getItem("couponDiscountAmount");

    if (appliedCouponCode && couponDiscountAmount) {
      return {
        isApplied: true,
        code: appliedCouponCode,
        discountAmount: parseFloat(couponDiscountAmount),
      };
    }

    return {
      isApplied: false,
      code: "",
      discountAmount: 0,
    };
  }

  /**
   * Check if bundle pricing should apply to a specific item
   */
  static shouldShowBundlePrice(
    itemId: number,
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): boolean {
    if (!bundleInfo.isBundle || !bundleInfo.primaryProductId) return false;

    // Don't apply bundle pricing to the primary product itself
    if (itemId === bundleInfo.primaryProductId) return false;

    // Check if primary product is in the cart
    const primaryProductInCart = checkOutItems.some(
      (item) => item.id === bundleInfo.primaryProductId
    );

    return primaryProductInCart;
  }

  /**
   * Get display price for an item
   * Uses discountedBundlePrice for items different from the primary/default item
   * Uses selling_price for the primary/default item
   * Exception: item id 3 (NFO registration) always uses selling_price
   * If only one item remains or primary product is not in cart, use selling_price (no bundle pricing)
   */
  static getDisplayPrice(
    item: ItemDetailsData,
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): number {
    // Exception: item id 3 (NFO registration) always uses selling_price
    if (item.id === 3) {
      console.log(
        `Item ${item.id} is NFO registration, always using selling_price:`,
        item.selling_price
      );
      return item.selling_price;
    }

    // If only one item in cart, no bundle pricing - use selling_price
    if (checkOutItems.length === 1) {
      console.log(
        `Item ${item.id} is only item in cart, using selling_price (no bundle):`,
        item.selling_price
      );
      return item.selling_price;
    }

    // Identify the primary/default item
    // If bundleInfo.primaryProductId exists, check if it's still in the cart
    // Otherwise, use the first item in checkOutItems as the primary item
    let primaryItemId: number | null = null;

    if (bundleInfo.primaryProductId) {
      // Check if the primary product is still in the cart
      const primaryProductInCart = checkOutItems.some(
        (item) => item.id === bundleInfo.primaryProductId
      );
      if (primaryProductInCart) {
        primaryItemId = bundleInfo.primaryProductId;
      } else {
        // Primary product was removed, use first item as primary
        primaryItemId = checkOutItems.length > 0 ? checkOutItems[0].id : null;
      }
    } else {
      // No bundle info, use first item as primary
      primaryItemId = checkOutItems.length > 0 ? checkOutItems[0].id : null;
    }

    const isPrimaryItem = primaryItemId !== null && item.id === primaryItemId;

    console.log(`getDisplayPrice for item ${item.id}:`, {
      id: item.id,
      name: item.name,
      selling_price: item.selling_price,
      discountedBundlePrice: item.discountedBundlePrice,
      bundleInfo: bundleInfo,
      primaryItemId: primaryItemId,
      isPrimaryItem: isPrimaryItem,
      itemsInCart: checkOutItems.length,
    });

    // For the primary item, use selling_price
    // For items different from the primary item, use discountedBundlePrice
    if (isPrimaryItem) {
      console.log(
        `Item ${item.id} is primary item, using selling_price:`,
        item.selling_price
      );
      return item.selling_price;
    } else {
      // Use discountedBundlePrice if available, otherwise fall back to selling_price
      const priceToUse =
        item.discountedBundlePrice > 0
          ? item.discountedBundlePrice
          : item.selling_price;
      console.log(
        `Item ${item.id} is not primary item, using discountedBundlePrice:`,
        priceToUse
      );
      return priceToUse;
    }
  }

  /**
   * Calculate pricing for a single item
   * Uses discountedBundlePrice for items different from the primary/default item
   * Exception: item id 3 (NFO registration) never uses bundle pricing
   */
  static calculateItemPricing(
    item: ItemDetailsData,
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): ItemPricingResult {
    const displayPrice = this.getDisplayPrice(item, bundleInfo, checkOutItems);

    // Exception: item id 3 (NFO registration) never uses bundle pricing
    if (item.id === 3) {
      const discountAmount = item.base_price - displayPrice;
      const discountPercentage = Math.round(
        (discountAmount / item.base_price) * 100
      );
      return {
        itemId: item.id,
        basePrice: item.base_price,
        displayPrice: displayPrice,
        isBundlePriceApplied: false,
        discountAmount: discountAmount,
        discountPercentage: discountPercentage,
      };
    }

    // Identify the primary/default item
    const primaryItemId =
      bundleInfo.primaryProductId ??
      (checkOutItems.length > 0 ? checkOutItems[0].id : null);
    const isPrimaryItem = primaryItemId !== null && item.id === primaryItemId;

    // Set isBundlePriceApplied to true if we're using discountedBundlePrice (non-primary item)
    const isBundlePriceApplied =
      !isPrimaryItem &&
      item.discountedBundlePrice > 0 &&
      item.discountedBundlePrice !== item.selling_price;

    const discountAmount = item.base_price - displayPrice;
    const discountPercentage = Math.round(
      (discountAmount / item.base_price) * 100
    );

    return {
      itemId: item.id,
      basePrice: item.base_price,
      displayPrice: displayPrice,
      isBundlePriceApplied: isBundlePriceApplied,
      discountAmount: discountAmount,
      discountPercentage: discountPercentage,
    };
  }

  /**
   * Calculate prepaid discount amount
   */
  // static calculatePrepaidDiscount(price: number): number {
  //   return Math.round(price * 0.05); // 5% discount
  // }

  /**
   * Calculate complete cart pricing
   */
  static calculateCartPricing(
    checkOutItems: ItemDetailsData[],
    bundleInfo: BundleInfo,
    couponData?: CouponData,
    paymentType: "prepaid" | "cod" = "cod"
  ): CartPricingSummary {
    console.log("=== PRICING DEBUG ===");
    console.log("checkOutItems:", checkOutItems);
    console.log("bundleInfo:", bundleInfo);

    // Calculate individual item pricing
    const items = checkOutItems.map((item) => {
      const itemPricing = this.calculateItemPricing(
        item,
        bundleInfo,
        checkOutItems
      );
      console.log(`Item ${item.id} (${item.name}):`, {
        basePrice: itemPricing.basePrice,
        displayPrice: itemPricing.displayPrice,
        discountAmount: itemPricing.discountAmount,
        isBundlePriceApplied: itemPricing.isBundlePriceApplied,
        selling_price: item.selling_price,
        discountedBundlePrice: item.discountedBundlePrice,
      });
      return itemPricing;
    });

    // Calculate subtotals
    const basePrice = items.reduce((sum, item) => sum + item.basePrice, 0);
    const sellingPrice = items.reduce(
      (sum, item) => sum + item.displayPrice,
      0
    );

    console.log("Total basePrice:", basePrice);
    console.log("Total sellingPrice:", sellingPrice);
    // Note: discountedBundlePrice is used for non-primary items in getDisplayPrice()
    // bundleDiscount represents a separate calculated discount, which is 0
    const bundleDiscount = 0;

    // Calculate prepaid discount (5% of selling price after bundle discounts)
    // const prepaidDiscount =
    //   paymentType === "prepaid"
    //     ? this.calculatePrepaidDiscount(sellingPrice)
    //     : 0;
    const prepaidDiscount = 0; // Prepaid discount temporarily disabled
    const priceAfterPrepaidDiscount = sellingPrice - prepaidDiscount;

    console.log("prepaidDiscount:", prepaidDiscount);
    console.log("priceAfterPrepaidDiscount:", priceAfterPrepaidDiscount);

    // Get coupon data if not provided
    const coupon = couponData || this.getCouponData();
    const couponDiscount = coupon.isApplied ? coupon.discountAmount : 0;

    console.log("coupon:", coupon);
    console.log("couponDiscount:", couponDiscount);

    // Calculate final price
    const finalPrice = priceAfterPrepaidDiscount - couponDiscount;

    console.log("FINAL PRICE:", finalPrice);
    console.log("=== END PRICING DEBUG ===");
    const totalDiscount = basePrice - finalPrice;
    const discountPercentage = Math.round((totalDiscount / basePrice) * 100);

    const subtotal: PricingResult = {
      basePrice,
      sellingPrice,
      discountedPrice: priceAfterPrepaidDiscount,
      bundleDiscount,
      prepaidDiscount,
      couponDiscount,
      finalPrice,
      totalDiscount,
      discountPercentage,
    };

    return {
      items,
      subtotal,
      couponData: coupon,
      bundleInfo,
    };
  }

  /**
   * Get discount percentage for recommended items
   */
  static getRecommendedItemDiscountPercentage(item: RecommendedItem): number {
    return Math.round(
      ((item.base_price - item.selling_price) / item.base_price) * 100
    );
  }

  /**
   * Check if bundle pricing is active (always returns false - bundle pricing disabled)
   */
  static isBundlePricingActive(
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): boolean {
    // Bundle pricing is disabled - always return false
    return false;
  }

  /**
   * Get bundle pricing parameters for API calls
   * Returns giveBundleDiscount: true and primaryProductID when there are non-primary items
   * that should use discountedBundlePrice
   * Exception: item id 3 (NFO registration) is excluded from bundle discount calculations
   */
  static getBundleParams(
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): { giveBundleDiscount: boolean; primaryProductID?: number } {
    // Filter out item id 3 from bundle discount calculations
    const itemsForBundleDiscount = checkOutItems.filter(
      (item) => item.id !== 3
    );

    // If there are no items left after filtering (only item id 3), no bundle discount
    if (itemsForBundleDiscount.length === 0) {
      return {
        giveBundleDiscount: false,
        primaryProductID: undefined,
      };
    }

    // Identify the primary/default item (excluding item id 3)
    const primaryItemId =
      bundleInfo.primaryProductId ??
      (itemsForBundleDiscount.length > 0 ? itemsForBundleDiscount[0].id : null);

    // If we have a primary item and there are multiple items in cart (excluding item id 3),
    // we should give bundle discount for non-primary items
    const shouldGiveBundleDiscount =
      primaryItemId !== null &&
      itemsForBundleDiscount.length > 1 &&
      itemsForBundleDiscount.some((item) => item.id !== primaryItemId);

    return {
      giveBundleDiscount: shouldGiveBundleDiscount,
      primaryProductID: primaryItemId ?? undefined,
    };
  }

  /**
   * Calculate current amount for coupon validation (after bundle discounts and prepaid discount but before coupon)
   */
  static calculateCurrentAmount(
    checkOutItems: ItemDetailsData[],
    bundleInfo: BundleInfo,
    paymentType: "prepaid" | "cod" = "cod"
  ): number {
    const baseAmount = checkOutItems.reduce((total, item) => {
      const effectivePrice = this.getDisplayPrice(
        item,
        bundleInfo,
        checkOutItems
      );
      return total + effectivePrice;
    }, 0);

    // Apply prepaid discount if applicable
    // const prepaidDiscount =
    //   paymentType === "prepaid" ? this.calculatePrepaidDiscount(baseAmount) : 0;
    // return baseAmount - prepaidDiscount;
    return baseAmount; // Prepaid discount temporarily disabled
  }

  /**
   * Get applied coupon code from session storage
   */
  static getAppliedCouponCode(): string | undefined {
    const appliedCouponCode = sessionStorage.getItem("appliedCouponCode");
    return appliedCouponCode || undefined;
  }

  /**
   * Format price for display
   */
  static formatPrice(price: number): string {
    return `₹${price}`;
  }

  /**
   * Calculate COD charges
   */
  static calculateCODCharges(itemCount: number): number {
    return itemCount > 1 ? 150 : 100;
  }

  /**
   * Calculate final order total including COD charges
   */
  static calculateOrderTotal(
    finalPrice: number,
    paymentType: "prepaid" | "cod",
    itemCount: number
  ): number {
    console.log("🧮 calculateOrderTotal called:", {
      finalPrice,
      paymentType,
      itemCount,
    });
    const codCharges =
      paymentType === "cod" ? this.calculateCODCharges(itemCount) : 0;
    const total = finalPrice + codCharges;
    console.log("🧮 calculateOrderTotal returning:", total);
    return total;
  }
}

export default PricingService;
